import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { TTags } from "components/Title";
import { isPriceHistoryEntryConnectedToActivePeriod } from "components/Title/PriceHistory/utils";
import type { TNewTitle } from "components/TitleFormContainer/types";
import type { IBeneficiar } from "containers/Beneficiars/types";
import type {
    GetTitleRevenueSharesResponse,
    ImportBatchSnapshotsPayload,
    ImportBatchSnapshotsResponse,
    ITitle,
} from "containers/Titles/types";
import { base64ImageEncode, downloadApiExport, getDateForFileName, isFinalReq } from "helpers/utils";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";
import type { RootState } from "store/store";

import { type CreatedTermListEntry, insertCreatedTerm } from "./createdTerm";
import type { ISalesByChannels, ITerm } from "./managementStore";
import { updateSale } from "./managementStore";
import type { TPeriod } from "./periodStore";
import {
    appendTitleTag,
    buildTagFromPostPayload,
    normalizeTitleTags,
    patchTitleInList,
    removeTitleTag,
} from "./titleTagSync";
import type { IAnalyticalCodes, TFetchStatus, TParamsPagination } from "./types";

const colors = {
    colors: [
        { color: "#9185CB", background: "#B8AEE921" },
        { color: "#848B4F", background: "#F4FDA994" },
        { color: "#B26A80", background: "#F2BFCD40" },
        { color: "#4E6DA0", background: "#A0BFF638" },
        { color: "#87B298", background: "#ECFAED" },
    ],
    getRandColor() {
        return this.colors[Math.floor(Math.random() * this.colors.length)];
    },
};

const formatDTO = (data: ITitle): ITitle => {
    return {
        ...data,
        tags: normalizeTitleTags(data.tags),
        defaultPriceHistory: data.defaultPriceHistory?.map((d) => ({
            ...d,
            channelId: undefined,
        })),
    };
};

export const selectTitleDefaultPrice = (state: RootState) =>
    (state.titles.title?.defaultPriceHistory?.find((e) =>
        isPriceHistoryEntryConnectedToActivePeriod(e, state.period.activePeriodData),
    )?.price ??
        state.priceHistory.draftEntries.find(
            (p) =>
                p.channelId === undefined &&
                isPriceHistoryEntryConnectedToActivePeriod(p, state.period.activePeriodData),
        )?.price ??
        state.titles.title?.price ??
        0) / 100;

export const fetchTitles = createAsyncThunk(
    "titles/getTitles",
    async (options: TParamsPagination, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/title", { params: options });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response);
        }
    },
);

export const fetchTitlesAutocomplete = createAsyncThunk(
    "titles/getTitlesAutocomplete",
    async (options: TParamsPagination, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/title", { params: options });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchLastPublications = createAsyncThunk(
    "titles/fetchLastPublications",
    async (options: TParamsPagination, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/title/article/latest", {
                params: options,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const sendPicture = createAsyncThunk("titles/sendPicture", async (file: File) => {
    try {
        const base64string = await base64ImageEncode(file);
        const payload = {
            name: file.name,
            mime: file.type,
            size: file.size,
            content: base64string,
        };
        const response = await toast.promise(
            axios.post<{ imagePath: string }>(
                import.meta.env.REACT_APP_API_ENDPOINT + "/images/upload-base64",
                payload,
            ),
            {
                pending: `${t("Upload.Pending")}`,
                success: `${t("Upload.Successful")}`,
                error: `${t("Upload.Error")}`,
            },
        );
        return response.data.imagePath;
    } catch (e) {
        console.log("error", e);
        return "";
    }
});

export const fetchTitle = createAsyncThunk("titles/getTitle", async (id: string | number, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/title/${id}`);
        return response.data.result;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});
export const fetchDeleteTitle = createAsyncThunk("titles/deleteTitle", async (id: number, { rejectWithValue }) => {
    try {
        const response = await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/title/${id}`);
        return response.data.result;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});
export const fetchDeleteTitleOverridenChannel = createAsyncThunk(
    "titles/deleteTitleOverridenChannel",
    async ({ titleId, channelId }: { titleId: number; channelId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/channels/${channelId}`,
            );
            return response.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAssociatedBeneficiars = createAsyncThunk(
    "titles/getAssociatedBeneficiars",
    async (id: number, { rejectWithValue }) => {
        const fakeData = {
            photo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTFhSnoly9J1ySaRA45scYB4Q1otdhWAXtLlg&usqp=CAU",
            associated: "Rose Marie",
            channels: [
                {
                    photo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTFhSnoly9J1ySaRA45scYB4Q1otdhWAXtLlg&usqp=CAU",
                },
                {
                    photo: "https://image.shutterstock.com/image-vector/people-icon-isolated-flat-design-260nw-401277397.jpg",
                },
            ],
            end: "2022-12-31",
            balance: 100,
        };
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/title/${id}/beneficiars`);
            const fakeDataAppendedResponse = response.data.result.map((item: any) => ({ ...item, ...fakeData }));
            return fakeDataAppendedResponse;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchTermTitles = createAsyncThunk("titles/getTermTitles", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/sales/titles`);
        return response.data.result;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const fetchTitlesByBeneficiar = createAsyncThunk(
    "beneficiars/getTitlesByBeneficiar",
    async (options: TParamsPagination, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/titles`, {
                params: options,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchCreateNewTitle = createAsyncThunk(
    "titles/createTitle",
    async ({ data }: { data: TNewTitle }, { rejectWithValue }) => {
        const { tags, ...titleData } = data;
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/title", { data: titleData });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchUpdateTitle = createAsyncThunk(
    "titles/updateTitle",
    async ({ data, id }: { data: TNewTitle; id: number }, { dispatch, rejectWithValue }) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/title/${id}`, data);
            if (response.status !== 200) {
                throw Error;
            }
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchUpdateTitlePress = createAsyncThunk(
    "titles/updateTitlePress",
    async ({ data, id }: { data: TNewTitle; id: number }, { dispatch, rejectWithValue }) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/title/${id}`, data);
            if (response.status !== 200) {
                throw Error;
            }
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchTitleByBeneficiar = createAsyncThunk(
    "titles/getTitleByBeneficiar",
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/titles/${id}`,
            );
            return response.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const exportTitleList = createAsyncThunk("beneficiars/exportTitleList", async () => {
    const response = await toast.promise(
        axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/title/export`, {
            headers: { Accept: "*/*" },
        }),
        {
            pending: `${t("Export.Pending")}`,
            success: `${t("Export.Successful")}`,
            error: `${t("Export.Error")}`,
        },
    );

    const fileName = `export_titles_${getDateForFileName()}.xlsx`;
    downloadApiExport(response.data, fileName);
});

export const exportEan13MatchingChannel = createAsyncThunk("beneficiars/exportEan13MatchingChannel", async () => {
    const response = await toast.promise(
        axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/title/export/ean13-matching-channel`, {
            headers: { Accept: "*/*" },
        }),
        {
            pending: `${t("Export.Pending")}`,
            success: `${t("Export.Successful")}`,
            error: `${t("Export.Error")}`,
        },
    );

    const fileName = `export_ean13_channels_${getDateForFileName()}.xlsx`;
    downloadApiExport(response.data, fileName);
});

export const fetchStatementsFromTitleBeneficiarPortal = createAsyncThunk(
    "beneficiars/fetchStatementsFromTitleBeneficiarPortal",
    async (titleId: number, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/titles/${titleId}/statements`,
            );
            return response.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchTags = createAsyncThunk("tags/fetchTag", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/title/tags`);
        return response.data.tags;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const postNewTags = createAsyncThunk(
    "tags/postTag",
    async (
        { titleId, tag }: { titleId: string | undefined; tag: { name: string; color: number } },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/tag`, tag);
            return { ...tag, titleId, tagId: response.data.tagId[0] };
            //   const returnValue = response.data.color
            //   ? {...tag, titleId, tagId: response.data.tagId, color: response.data.color}
            //   : {...tag, titleId, tagId: response.data.tagId}
            // return returnValue
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteTag = createAsyncThunk(
    "tags/deleteTag",
    async ({ titleId, tagId }: { titleId: number; tagId: number }, { rejectWithValue }) => {
        try {
            await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/tag/${tagId}`);
            return { titleId, tagId };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchTermAssociatedToTitle = createAsyncThunk(
    "titles/fetchTermAssociatedToTitle",
    async ({ titleId }: { titleId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/terms`);
            return response.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchTitlesNames = createAsyncThunk("titles/names", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/title/lightened`);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

// Fetch all analytical codes
export const fetchAnalyticalCodes = createAsyncThunk("titles/fetchAnalyticalCodes", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/title/analysis-code`);
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

// Fetch one specific analytical code
export const fetchAnalyticalCode = createAsyncThunk(
    "titles/fetchAnalyticalCode",
    async ({ id }: { id: string }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/title/analysis-code/${id}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Create a specific code
export const createAnalyticalCode = createAsyncThunk(
    "titles/createAnalyticalCode",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/title/analysis-code`, data);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Update a specific code
export const updateAnalyticalCode = createAsyncThunk(
    "titles/updateAnalyticalCode",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/title/analysis-code/${data.id}`,
                data,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Delete a specific code
export const deleteAnalyticalCode = createAsyncThunk(
    "titles/deleteAnalyticalCode",
    async (data: any, { rejectWithValue }) => {
        try {
            await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/title/analysis-code/${data.id}`, data);
            return data.id;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchISBNTitleInfo = createAsyncThunk(
    "titles/fetchISBNTitleInfo",
    async ({ ISBN }: { ISBN: string }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/title/explore?isbn=${ISBN}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response);
        }
    },
);

export const sendTitleCover = createAsyncThunk(
    "titles/sendTitleCover",
    async ({ url }: { url: string | FileList }, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/images/upload-link`, { url });
            return response.data.imagePath;
        } catch (error: any) {
            return rejectWithValue(error.response);
        }
    },
);

// Lists all existing groups
export const fetchTitleGroups = createAsyncThunk("titles/fetchTitleGroups", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/title/groups");
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

// Creates a new group of titles
export const createTitleGroup = createAsyncThunk(
    "titles/createTitleGroup",
    async ({ name }: { name: string }, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/title/groups", { name });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Get sales of a title:
export const fetchTitleSales = createAsyncThunk(
    "titles/fetchTitleSales",
    async ({ titleId, options }: { titleId: number; options: TParamsPagination }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/sales`, {
                params: options,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Get subscription titles with revenue shares:
export const fetchSubscriptionTitles = createAsyncThunk(
    "titles/fetchSubscriptionTitles",
    async (
        { channelId, periodId, options }: { channelId: number; periodId: number; options?: TParamsPagination },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.get<GetTitleRevenueSharesResponse>(
                import.meta.env.REACT_APP_API_ENDPOINT_V2 +
                    `/subscription/channel/${channelId}/period/${periodId}/titles`,
                { params: options },
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Verify subscription snapshots before import:
export const verifyImportSubscriptionSnapshots = createAsyncThunk(
    "titles/verifyImportSubscriptionSnapshots",
    async (payload: ImportBatchSnapshotsPayload, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT_V2 + `/subscription/snapshots/batch/verify`,
                payload,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Batch import subscription snapshots:
export const importBatchSnapshots = createAsyncThunk(
    "titles/importBatchSnapshots",
    async (payload: ImportBatchSnapshotsPayload, { rejectWithValue }) => {
        try {
            const response = await axios.post<ImportBatchSnapshotsResponse>(
                import.meta.env.REACT_APP_API_ENDPOINT_V2 + `/subscription/snapshots/batch`,
                payload,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// export const fetchLastStatements = createAsyncThunk(
//   'beneficiarPortal/fetchLastStatements',
//   async () => {
//     const response = await axios.get(import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/titles/statements/latest`);
//     return response.data;
//   }
// )

// export const fetchBPStatements = createAsyncThunk(
//   'beneficiarPortal/fetchBPStatements',
//   async () => {
//     const response = await axios.get(import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/titles/statements`);
//     return response.data;
//   }
// )

export interface TitleGroup {
    id: number;
    name: string;
    titles?: ITitle[];
}

export interface TitleState {
    sendPictureStatus: TFetchStatus;
    updateTitleStatus: TFetchStatus;
    createTitleStatus: TFetchStatus;
    deleteTitleStatus: TFetchStatus;
    deleteTitleOverridenChannelStatus: TFetchStatus;
    title: ITitle | null;
    titleStatus: TFetchStatus;
    titlesStatus: TFetchStatus;
    fetchTitlesReqId: string;
    titles: ITitle[];
    titlesAutocomplete: ITitle[];
    countTitles: number;
    lastPublications: ITitle[];
    lastAddedTagId: number;
    tags: TTags[] | null;
    termTitles: any[];
    associatedBeneficiars: IBeneficiar[];
    beneficiarTitle: ITitle | object;
    beneficiarTitleStatus: TFetchStatus;
    beneficiarTitles: ITitle[];
    beneficiarTitlesStatus: TFetchStatus;
    countBeneficiarTitles: number;
    beneficiarTitleStatement: TPeriod[];
    termAssociatedToTitle: ITerm[];
    termAssociatedToTitleStatus: TFetchStatus;
    termAssociatedToTitleTitleId: number | null;
    titlesNames: ITitle[];
    titlesNamesStatus: TFetchStatus;
    codes: IAnalyticalCodes[];
    codesStatus: TFetchStatus;
    code: IAnalyticalCodes | null;
    codeStatus: TFetchStatus;
    ISBNinfo: any;
    dilicomFetchStatus: TFetchStatus;
    titleCover: string;
    titleGroups: TitleGroup[];
    titleGroupsStatus: TFetchStatus;
    titleSales: ISalesByChannels[];
    titleSalesStatus: TFetchStatus;
    titleSalesCount: number;
    titleFirstSaleDate: Date | undefined;
    importBatchSnapshotsStatus: TFetchStatus;
}

export const initialState: TitleState = {
    title: null,
    sendPictureStatus: "idle",
    updateTitleStatus: "idle",
    createTitleStatus: "idle",
    deleteTitleStatus: "idle",
    deleteTitleOverridenChannelStatus: "idle",
    titlesStatus: "idle",
    fetchTitlesReqId: "",
    titleStatus: "idle",
    titles: [],
    titlesAutocomplete: [],
    countTitles: 0,
    lastPublications: [],
    lastAddedTagId: -1,
    tags: null,
    termTitles: [],
    associatedBeneficiars: [],
    beneficiarTitle: {},
    beneficiarTitleStatus: "idle",
    beneficiarTitles: [],
    beneficiarTitlesStatus: "idle",
    countBeneficiarTitles: 0,
    beneficiarTitleStatement: [],
    termAssociatedToTitle: [],
    termAssociatedToTitleStatus: "idle",
    termAssociatedToTitleTitleId: null,
    titlesNames: [],
    titlesNamesStatus: "idle",
    codes: [],
    codesStatus: "idle",
    code: null,
    codeStatus: "idle",
    ISBNinfo: {},
    dilicomFetchStatus: "idle",
    titleCover: "",
    titleGroups: [],
    titleGroupsStatus: "idle",
    titleSales: [],
    titleSalesStatus: "idle",
    titleSalesCount: 0,
    /**
     * Date of the very first sale on title (might be earlier than start of first period)
     */
    titleFirstSaleDate: undefined,
    importBatchSnapshotsStatus: "idle",
};

export const titlesSlice = createSlice({
    name: "titles",
    initialState,
    reducers: {
        setDeleteTitleIdleStatus(state) {
            state.deleteTitleStatus = "idle";
        },
        setDeleteTitleOverridenChannelIdleStatus(state) {
            state.deleteTitleOverridenChannelStatus = "idle";
        },
        deleteTitle(state) {
            state.title = null;
        },
        removeTitlesByIds(state, action: PayloadAction<number[]>) {
            if (action.payload.length === 0) return;
            const removedIds = new Set(action.payload);
            const previousCount = state.titles.length;
            state.titles = state.titles.filter((title) => !removedIds.has(title.id));
            state.countTitles = Math.max(0, state.countTitles - (previousCount - state.titles.length));
            if (state.title && removedIds.has(state.title.id)) {
                state.title = null;
            }
        },
        deleteByTerm(state, action: PayloadAction<number>) {
            state.termAssociatedToTitle = state.termAssociatedToTitle.filter((term) => term.id !== action.payload);
            state.associatedBeneficiars = state.associatedBeneficiars.filter(
                (beneficiary) => beneficiary?.termId !== action.payload,
            );
        },
        addCreatedTerm(state, action: PayloadAction<CreatedTermListEntry>) {
            const titleId = action.payload.titleId;
            if (
                titleId == null ||
                state.termAssociatedToTitleTitleId !== titleId ||
                state.termAssociatedToTitleStatus !== "succeeded"
            ) {
                return;
            }
            state.termAssociatedToTitle = insertCreatedTerm(state.termAssociatedToTitle, action.payload, {
                avatar: "beneficiary",
                sortKey: (term) => term.isbn ?? "",
            });
        },
        updatePublicationCount(state, action) {
            state.titles = state.titles.map((title) => {
                if (title.id == action.payload && title.nbArticles) title.nbArticles -= 1;
                return title;
            });
        },
        resetTitles(state) {
            state.title = null;
            state.titleStatus = "idle";
        },
        prepareTitleFetch(state) {
            state.titleStatus = "idle";
        },
        setTitlePreview(state, action: PayloadAction<Partial<ITitle> & { id: number }>) {
            if (state.title?.id !== action.payload.id) {
                state.termAssociatedToTitle = [];
                state.termAssociatedToTitleStatus = "idle";
                state.termAssociatedToTitleTitleId = null;
                state.titleStatus = "idle";
            }
            state.title = {
                ...(state.title?.id === action.payload.id ? state.title : {}),
                ...action.payload,
            } as ITitle;
        },
        addNoteToAssociatedTerm(state, action) {
            state.termAssociatedToTitle = state.termAssociatedToTitle.map((term) => {
                if (term.id == action.payload.termId) {
                    term.notesCount ? term.notesCount++ : (term.notesCount = 1);
                }
                return term;
            });
        },
        deleteNoteFromAssociatedTerm(state, action) {
            state.termAssociatedToTitle = state.termAssociatedToTitle.map((term) => {
                if (term.id == action.payload.termId) {
                    term.notesCount ? term.notesCount-- : (term.notesCount = 0);
                }
                return term;
            });
        },
        resetTermAssociatedToTitles(state) {
            state.termAssociatedToTitle = [];
            state.termAssociatedToTitleTitleId = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchTitles.fulfilled, (state, action) => {
            const reqId = action.meta.requestId;
            if (!isFinalReq(state.fetchTitlesReqId, reqId)) return;
            state.titlesStatus = "succeeded";
            const titles = [
                ...action.payload.entries.map((item: ITitle) => ({
                    ...item,
                    tags: normalizeTitleTags(item.tags),
                    ...colors.getRandColor(),
                })),
            ];
            if (action.payload.offset) {
                state.titles = state.titles.concat(titles);
            } else {
                state.titles = titles;
            }
            state.countTitles = +action.payload.count;
            state.ISBNinfo = []; // reset Dilicom data if user got fetched info by putting an ISBN and then come back to the list of titles
        });
        builder.addCase(fetchTitles.pending, (state, action) => {
            const reqId = action.meta.requestId;
            state.fetchTitlesReqId = reqId;
            state.titlesStatus = "pending";
        });
        builder.addCase(fetchTitles.rejected, (state, { payload }) => {
            state.titlesStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchTitlesAutocomplete.fulfilled, (state, action) => {
            const titles = action.payload.entries;
            if (action.payload.offset) state.titlesAutocomplete = state.titlesAutocomplete.concat(titles);
            else state.titlesAutocomplete = titles;
        });
        builder.addCase(fetchTitlesAutocomplete.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchLastPublications.fulfilled, (state, action) => {
            state.titlesStatus = "succeeded";
            state.lastPublications = action.payload.data;
        });
        builder.addCase(fetchLastPublications.pending, (state, action) => {
            state.titlesStatus = "pending";
        });
        builder.addCase(fetchLastPublications.rejected, (state, { payload }) => {
            state.titlesStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchAssociatedBeneficiars.fulfilled, (state, action) => {
            state.associatedBeneficiars = action.payload;
        });
        builder.addCase(fetchAssociatedBeneficiars.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchTermTitles.fulfilled, (state, action) => {
            state.termTitles = action.payload;
        });
        builder.addCase(fetchTermTitles.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchTitlesByBeneficiar.pending, (state, action) => {
            state.beneficiarTitlesStatus = "pending";
        });
        builder.addCase(fetchTitlesByBeneficiar.fulfilled, (state, action) => {
            state.beneficiarTitlesStatus = "succeeded";
            state.beneficiarTitles = action.payload.entries ?? [];
            state.countBeneficiarTitles = action.payload.entries?.length ?? 0;
        });
        builder.addCase(fetchTitlesByBeneficiar.rejected, (state, { payload }) => {
            state.beneficiarTitlesStatus = "failed";
            state.beneficiarTitles = [];
            state.countBeneficiarTitles = 0;
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchStatementsFromTitleBeneficiarPortal.fulfilled, (state, action) => {
            state.beneficiarTitleStatement = action.payload;
        });
        builder.addCase(fetchStatementsFromTitleBeneficiarPortal.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchTitle.fulfilled, (state, action) => {
            if (state.title?.id != null && state.title.id !== Number(action.meta.arg)) return;
            const data = action.payload;
            const channels = data?.overriddenChannelsData;
            /**
             * Face the issue when there is channels with country markets but no "main channel"
             * ( a channel where the countryMarket key value = "");
             * format the channel data to add the parent channel.
             */
            channels?.forEach((channel) => {
                if (channel.countryMarket) {
                    const mainChannel = channels?.find((ch) => ch.id === channel.id && !ch.countryMarket);
                    if (!mainChannel)
                        channels.push({ ...channel, countryMarket: "", price: 0, currency: data.currency });
                }
            });
            state.title = formatDTO(data);
            state.titleStatus = "succeeded";
        });
        builder.addCase(fetchTitle.pending, (state, action) => {
            if (state.title?.id != null && state.title.id !== Number(action.meta.arg)) return;
            state.titleStatus = "pending";
        });
        builder.addCase(fetchTitle.rejected, (state, { payload, meta }) => {
            if (state.title?.id != null && state.title.id !== Number(meta.arg)) return;
            state.titleStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchTitleByBeneficiar.fulfilled, (state, action) => {
            state.beneficiarTitle = action.payload;
            state.beneficiarTitleStatus = "succeeded";
        });
        builder.addCase(fetchTitleByBeneficiar.pending, (state) => {
            state.beneficiarTitleStatus = "pending";
        });
        builder.addCase(fetchTitleByBeneficiar.rejected, (state, { payload }) => {
            state.beneficiarTitleStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchDeleteTitle.pending, (state) => {
            state.deleteTitleStatus = "pending";
        });
        builder.addCase(fetchDeleteTitle.fulfilled, (state, action) => {
            state.titles = state.titles.filter((t) => t.id !== action.meta.arg);
            state.countTitles = state.countTitles - 1;
            // state.title = null;
            toast.success(`${t("Titles.SuccessfullyDeleted")}`);
            state.deleteTitleStatus = "succeeded";
        });
        builder.addCase(fetchDeleteTitle.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.deleteTitleStatus = "failed";
        });

        builder.addCase(fetchDeleteTitleOverridenChannel.pending, (state) => {
            state.deleteTitleOverridenChannelStatus = "pending";
        });
        builder.addCase(fetchDeleteTitleOverridenChannel.fulfilled, (state, action) => {
            toast.success(`${t("Titles.SuccessfullyDeletedOverridenChannel")}`);
            state.deleteTitleOverridenChannelStatus = "succeeded";
        });
        builder.addCase(fetchDeleteTitleOverridenChannel.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.deleteTitleOverridenChannelStatus = "failed";
        });

        builder.addCase(fetchCreateNewTitle.pending, (state) => {
            state.createTitleStatus = "pending";
        });
        builder.addCase(fetchCreateNewTitle.fulfilled, (state, action) => {
            toast.success(`${t("NewTitle.TitleAdded")}`);
            const createdTitleId = action.payload.result.result.createdTitleId;
            const newTitle = {
                id: createdTitleId,
                distributorReference: "",
                lastModifiedDate: action.meta.arg.data.creationDate?.getTime() ?? new Date().getTime(),
                ...action.meta.arg.data,
                creationDate: action.meta.arg.data.creationDate?.getTime() ?? new Date().getTime(),
            };
            state.title = formatDTO(action.payload.result.result);
            //@ts-expect-error Missing properties from 'WritableDraft<ITitle>': term, analysisCode, hasArchive, sku
            state.titles = [newTitle, ...state.titles];
            //@ts-expect-error Missing properties from 'WritableDraft<ITitle>': term, analysisCode, hasArchive, sku
            state.titlesAutocomplete = [newTitle, ...state.titlesAutocomplete];
            state.ISBNinfo = [];
            state.createTitleStatus = "succeeded";
        });
        builder.addCase(fetchCreateNewTitle.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.createTitleStatus = "failed";
        });
        builder.addCase(fetchUpdateTitle.pending, (state) => {
            state.updateTitleStatus = "pending";
        });
        builder.addCase(fetchUpdateTitle.fulfilled, (state, action) => {
            state.titles = state.titles.map((t) => {
                if (t.id === action.meta.arg.id) {
                    t.name = action.meta.arg.data.name;
                    t.ean13 = action.meta.arg.data.ean13;
                    t.avatarUrl = action.meta.arg.data.avatarUrl;
                    t.releaseDate = action.meta.arg?.data.releaseDate as string | Date;
                    t.workId = action.meta.arg.data.workId ?? t.workId;
                }
                return t;
            });
            state.title = formatDTO(action.payload.data);
            state.updateTitleStatus = "succeeded";
            toast.success(`${t("NewTitle.TitleUpdated")}`);
        });
        builder.addCase(fetchUpdateTitle.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.updateTitleStatus = "failed";
        });
        builder.addCase(fetchUpdateTitlePress.pending, (state) => {
            state.updateTitleStatus = "pending";
        });

        builder.addCase(fetchUpdateTitlePress.fulfilled, (state, action) => {
            toast.success(`${t("NewTitle.TitleUpdated")}`);
            state.updateTitleStatus = "succeeded";
            state.titles = state.titles.map((t) => {
                if (t.id === action.meta.arg.id) {
                    t.name = action.meta.arg.data.name;
                    t.ean13 = action.meta.arg.data.ean13;
                    t.avatarUrl = action.meta.arg.data.avatarUrl;
                }
                return t;
            });
            let currentTitle = JSON.parse(JSON.stringify(state.title));
            currentTitle = {
                ...currentTitle,
                ...action.meta.arg.data,
                customFields: action.payload.data.customFields,
                overriddenChannelsData: [{ ...action.meta.arg.data.overriddenChannelsData[0], type: 3 }],
            };
            state.title = formatDTO(currentTitle);
        });
        builder.addCase(fetchUpdateTitlePress.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.updateTitleStatus = "failed";
        });

        builder.addCase(sendPicture.pending, (state) => {
            state.sendPictureStatus = "pending";
        });
        builder.addCase(sendPicture.fulfilled, (state) => {
            state.sendPictureStatus = "succeeded";
        });
        builder.addCase(sendPicture.rejected, (state, action) => {
            state.sendPictureStatus = "failed";
        });

        builder.addCase(fetchTags.fulfilled, (state, action) => {
            state.tags = action.payload;
        });
        builder.addCase(fetchTags.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(postNewTags.pending, () => {});
        builder.addCase(postNewTags.fulfilled, (state, action) => {
            toast.success(`${t("TagsFilter.CreateTagSuccess")}`);
            if (!action.payload.titleId) return;
            const newTag = buildTagFromPostPayload(action.payload);
            const titleId = +action.payload.titleId;

            state.titles = patchTitleInList(state.titles, titleId, (title) => ({
                ...title,
                tags: appendTitleTag(title.tags, newTag),
            }));

            if (state.title?.id === titleId) {
                state.title.tags = appendTitleTag(state.title.tags, newTag);
            }
            if (state.tags != null) state.tags?.push(newTag);
            else state.tags = [newTag];
            state.lastAddedTagId = newTag.id;
        });
        builder.addCase(postNewTags.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(deleteTag.pending, () => {});
        builder.addCase(deleteTag.fulfilled, (state, action) => {
            toast.success(`${t("TagsFilter.DeleteTagSuccess")}`);
            if (action.payload.titleId === undefined) return;
            const titleId = +action.payload.titleId;
            const tagId = +action.payload.tagId;

            state.titles = patchTitleInList(state.titles, titleId, (title) => ({
                ...title,
                tags: removeTitleTag(title.tags, tagId),
            }));

            if (state.title?.id === titleId && state.title.tags) {
                state.title.tags = removeTitleTag(state.title.tags, tagId);
            }
        });
        builder.addCase(deleteTag.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchTermAssociatedToTitle.pending, (state) => {
            state.termAssociatedToTitleStatus = "pending";
        });
        builder.addCase(fetchTermAssociatedToTitle.rejected, (state, { payload }) => {
            state.termAssociatedToTitleStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchTermAssociatedToTitle.fulfilled, (state, action) => {
            state.termAssociatedToTitle = action.payload;
            state.termAssociatedToTitleStatus = "succeeded";
            state.termAssociatedToTitleTitleId = action.meta.arg.titleId;
        });

        builder.addCase(fetchTitlesNames.pending, (state) => {
            state.titlesNamesStatus = "pending";
        });
        builder.addCase(fetchTitlesNames.fulfilled, (state, action) => {
            state.titlesNames = action.payload.entries;
            state.titlesNamesStatus = "succeeded";
        });
        builder.addCase(fetchTitlesNames.rejected, (state, { payload }) => {
            state.titlesNamesStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        // Fetching all analytical codes
        builder.addCase(fetchAnalyticalCodes.fulfilled, (state, action) => {
            state.codes = action.payload;
            state.codesStatus = "succeeded";
        });
        builder.addCase(fetchAnalyticalCodes.pending, (state) => {
            state.codes = [];
            state.codesStatus = "pending";
        });
        builder.addCase(fetchAnalyticalCodes.rejected, (state, { payload }) => {
            state.codes = [];
            state.codesStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        // Fetching one specific analytical code
        builder.addCase(fetchAnalyticalCode.fulfilled, (state, action) => {
            state.code = action.payload;
            state.codeStatus = "succeeded";
        });
        builder.addCase(fetchAnalyticalCode.pending, (state) => {
            state.code = null;
            state.codeStatus = "pending";
        });
        builder.addCase(fetchAnalyticalCode.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.code = null;
            state.codeStatus = "failed";
        });

        // Fetching ISBN info
        builder.addCase(fetchISBNTitleInfo.fulfilled, (state, action) => {
            state.ISBNinfo = action.payload;
            state.dilicomFetchStatus = "succeeded";
        });
        builder.addCase(fetchISBNTitleInfo.pending, (state, action) => {
            state.dilicomFetchStatus = "pending";
        });
        builder.addCase(fetchISBNTitleInfo.rejected, (state, action) => {
            state.ISBNinfo = {};
            state.dilicomFetchStatus = "failed";
        });

        builder.addCase(sendTitleCover.fulfilled, (state, action) => {
            state.titleCover = action.payload;
        });

        // Create an analytical code
        builder.addCase(createAnalyticalCode.fulfilled, (state, action) => {
            const newCode = action.payload;
            state.codes = [...state.codes, newCode];
            state.codesStatus = "succeeded";
            toast.success(`${t("AnalyticalCode.SuccessfulCreation")}`);
        });
        builder.addCase(createAnalyticalCode.pending, (state) => {
            state.codesStatus = "pending";
        });
        builder.addCase(createAnalyticalCode.rejected, (state, { payload }) => {
            state.codesStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        // Update an analytical code
        builder.addCase(updateAnalyticalCode.fulfilled, (state, action) => {
            state.codes = state.codes.map((code) => {
                if (code.id === action.payload.id) {
                    return { ...code, name: action.payload.name };
                } else {
                    return code;
                }
            });
            state.codesStatus = "succeeded";
            toast.success(`${t("AnalyticalCode.SuccessfulUpdate")}`);
        });
        builder.addCase(updateAnalyticalCode.pending, (state) => {
            state.codesStatus = "pending";
        });
        builder.addCase(updateAnalyticalCode.rejected, (state, { payload }) => {
            state.codesStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        // Delete an analytical code
        builder.addCase(deleteAnalyticalCode.fulfilled, (state, action) => {
            const newCodes = state.codes.filter((code) => code.id !== action.payload);
            state.codes = newCodes;
            state.codesStatus = "succeeded";
        });
        builder.addCase(deleteAnalyticalCode.pending, (state) => {
            state.codesStatus = "pending";
        });
        builder.addCase(deleteAnalyticalCode.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.codesStatus = "failed";
        });

        // Fetch all title groups
        builder.addCase(fetchTitleGroups.fulfilled, (state, action) => {
            state.titleGroups = action.payload;
            state.titleGroupsStatus = "succeeded";
        });
        builder.addCase(fetchTitleGroups.pending, (state) => {
            state.titleGroupsStatus = "pending";
        });
        builder.addCase(fetchTitleGroups.rejected, (state, { payload }) => {
            state.titleGroupsStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        // Create a title group
        builder.addCase(createTitleGroup.fulfilled, (state, action) => {
            state.titleGroups.push(action.payload);
            toast.success(`${t("CompensationGroups.TitleGroupCreated")}`);
        });
        builder.addCase(createTitleGroup.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchTitleSales.fulfilled, (state, { payload }) => {
            state.titleSales = payload.entries;
            state.titleSalesCount = payload.count;
            state.titleFirstSaleDate = payload.firstDate;
            state.titleSalesStatus = "succeeded";
        });
        builder.addCase(fetchTitleSales.pending, (state) => {
            state.titleSalesStatus = "pending";
        });
        builder.addCase(fetchTitleSales.rejected, (state, { payload }) => {
            state.titleSalesStatus = "failed";
            // Not sur if we should show toast as this is done in the background for our checks.
            // const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            // toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(updateSale.fulfilled, (state, action) => {
            state.titleSales = state.titleSales.map((sale) =>
                sale.id === action.meta.arg.salesId ? { ...sale, note: action.meta.arg.note ?? "" } : sale,
            );
        });

        builder.addCase(importBatchSnapshots.pending, (state) => {
            state.importBatchSnapshotsStatus = "pending";
        });
        builder.addCase(importBatchSnapshots.fulfilled, (state) => {
            state.importBatchSnapshotsStatus = "succeeded";
            toast.success(`${t("ImportData.SuccessToImportData")}`);
        });
        builder.addCase(importBatchSnapshots.rejected, (state, { payload }) => {
            state.importBatchSnapshotsStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(verifyImportSubscriptionSnapshots.fulfilled, (state) => {
            state.importBatchSnapshotsStatus = "succeeded";
        });
        builder.addCase(verifyImportSubscriptionSnapshots.rejected, (state, { payload }) => {
            state.importBatchSnapshotsStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        // builder.addCase(fetchLastStatements.fulfilled, (state, action) => {
        //   state.lastStatements = action.payload.result
        // });
        // builder.addCase(fetchLastStatements.rejected, (state, action) => {
        //   toast.error(`${t(`${action.error?.message}`)}`);
        // });

        // builder.addCase(fetchBPStatements.fulfilled, (state, action) => {
        //   state.lastStatements = action.payload.result
        // });
        // builder.addCase(fetchBPStatements.rejected, (state, action) => {
        //   toast.error(`${t(`${action.error?.message}`)}`);
        // });
    },
});
export const {
    setDeleteTitleIdleStatus,
    setDeleteTitleOverridenChannelIdleStatus,
    deleteTitle,
    removeTitlesByIds,
    deleteByTerm,
    addCreatedTerm,
    updatePublicationCount,
    resetTitles,
    prepareTitleFetch,
    setTitlePreview,
    addNoteToAssociatedTerm,
    deleteNoteFromAssociatedTerm,
    resetTermAssociatedToTitles,
} = titlesSlice.actions;
export default titlesSlice.reducer;
